import * as React from 'react';
import { printSltReport } from './printReport';
import { Card } from './OverviewContent';
const num = value => (value == null ? '—' : Number(value.toFixed(1)).toLocaleString('en-GB'));
const pct = value => (value == null ? '—' : `${Math.round(value)}%`);
const slots = ['Before school', 'Lunch', 'After school', 'Late'],
  days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
export function clubHeader(report: any, period: string) {
  if (!report) return 'School clubs';
  const capacity =
    report.capacityClubs < report.count
      ? `${num(report.capacity)} places in ${report.capacityClubs} clubs with a capacity`
      : `${num(report.capacity)} places`;
  return `${report.count} clubs · ${capacity} · ${period}`;
}
export function ClubSpark({ series, width = 96, height = 28 }: { series: any[]; width?: number; height?: number }) {
  const known = series.map((point, index) => ({ ...point, index })).filter(point => point.present != null);
  if (known.length < 2) return null;
  const values = known.map(point => point.present),
    min = Math.min(...values),
    max = Math.max(...values),
    first = values[0],
    last = values[values.length - 1];
  const colour = last > first ? 'var(--green-500)' : last < first ? 'var(--red-500)' : 'var(--blue-500)';
  let pen = false;
  const line = series
    .map((point, index) => {
      if (point.present == null) {
        pen = false;
        return '';
      }
      const command = pen ? 'L' : 'M';
      pen = true;
      return `${command}${2 + (index * (width - 4)) / Math.max(1, series.length - 1)} ${height -
        3 -
        ((point.present - min) * (height - 6)) / (max - min || 1)}`;
    })
    .join(' ');
  const end = known[known.length - 1];
  return (
    <svg
      className="slc-spark"
      style={{ width, height }}
      viewBox={`0 0 ${width} ${height}`}
      role="img"
      aria-label="Recorded weekly attendance"
    >
      <path d={line} fill="none" stroke={colour} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle
        cx={2 + (end.index * (width - 4)) / Math.max(1, series.length - 1)}
        cy={height - 3 - ((end.present - min) * (height - 6)) / (max - min || 1)}
        r="2.5"
        fill={colour}
      />
    </svg>
  );
}
export function ClubsContent({
  report,
  comparison,
  onStudents,
  onClub,
  onExport,
  onPeriod,
  onSetup,
  onReportStateChange
}: {
  report: any;
  comparison: any;
  onStudents: (query: any) => void;
  onClub: (club: any) => void;
  onExport: () => void;
  onPeriod: () => void;
  onSetup: () => void;
  onReportStateChange?: (state: any) => void;
}) {
  const [sort, setSort] = React.useState('attn'),
    [slot, setSlot] = React.useState<any>(null),
    [expanded, setExpanded] = React.useState(false);
  const table = React.useRef<HTMLElement>(null);
  React.useEffect(() => {
    onReportStateChange?.({
      sort,
      ...(slot?.clubIds ? { clubIds: slot.clubIds } : slot?.key ? { slotKey: slot.key } : {})
    });
  }, [slot, sort, onReportStateChange]);
  React.useEffect(() => {
    setSlot(null);
    setExpanded(false);
  }, [report]);
  const active = report.items.filter(club => club.sessions > 0),
    idle = report.items.filter(club => !club.sessions),
    previous = comparison?.comparison?.clubsReport;
  const target = report.thresholds.coverageTarget;
  const changeSort = (key: string, scroll = false) => {
    setSort(key);
    if (scroll) requestAnimationFrame(() => table.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  const severity = { high: 0, watch: 1, opportunity: 2 };
  const sorted = [...active]
    .filter(
      club =>
        !slot || (slot.clubIds ? slot.clubIds.includes(club.id) : club.slots.some(value => value.key === slot.key))
    )
    .sort((a, b) =>
      sort === 'az'
        ? a.name.localeCompare(b.name)
        : sort === 'busy'
        ? (b.averageAttendance ?? -1) - (a.averageAttendance ?? -1)
        : (a.signal ? severity[a.signalSeverity] ?? 3 : 4) - (b.signal ? severity[b.signalSeverity] ?? 3 : 4) ||
          (b.averageAttendance ?? -1) - (a.averageAttendance ?? -1) ||
          a.name.localeCompare(b.name)
    );
  const duplicate = (club: any) =>
    report.items.filter(item => item.name === club.name && item.scheduleLabel === club.scheduleLabel).length > 1;
  const subtitle = (club: any) => (
    <>
      {club.category || club.activity}
      {club.scheduleLabel ? ` · ${club.scheduleLabel}` : ''} ·{' '}
      <span className={!club.leader ? 'slc-noLead' : ''}>{club.leader || 'No lead set'}</span>
      {duplicate(club) ? ` · ID ${club.id}` : ''}
    </>
  );
  const items = [
    {
      key: 'reach',
      label: 'Students in a club',
      value: report.reach,
      unit: 'percent',
      sub:
        report.registered == null
          ? 'Participation records unavailable'
          : `${num(report.registered)} of ${num(report.roster)}`,
      action: () => onStudents({ area: 'clubs', segment: 'participating' }),
      delta: previous && report.reach != null && previous.reach != null ? report.reach - previous.reach : null
    },
    {
      key: 'capacity',
      label: 'Capacity used',
      value: report.capacityUsed,
      unit: 'percent',
      sub: report.capacityMeasured
        ? `${num(report.capacityAttendance)} of ${num(report.capacityMeasured)} places a week`
        : 'Needs attendance and capacity',
      action: () => changeSort('busy', true),
      delta:
        previous && report.capacityUsed != null && previous.capacityUsed != null
          ? report.capacityUsed - previous.capacityUsed
          : null
    },
    {
      key: 'decisions',
      label: 'Clubs needing a decision',
      value: report.decisions,
      sub: 'Declining, no registers or waitlist',
      action: () => changeSort('attn', true),
      delta: null
    },
    {
      key: 'coverage',
      label: 'Register coverage',
      value: report.coverage,
      unit: 'percent',
      sub: report.endedSessions ? `${report.missingRegisters} clubs missing registers` : 'No ended club sessions yet',
      action: () => changeSort('attn', true),
      delta: null
    }
  ];
  const renderRow = (club: any, collapsed = false) => (
    <tr
      key={club.id}
      tabIndex={0}
      role="button"
      aria-label={`Open ${club.name}${club.scheduleLabel ? ' · ' + club.scheduleLabel : ''}`}
      onClick={() => onClub(club)}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClub(club);
        }
      }}
    >
      <td>
        <b>{club.name}</b>
        <small>{subtitle(club)}</small>
      </td>
      <td>
        {collapsed ? (
          '—'
        ) : (
          <>
            {num(club.averageAttendance)}
            {club.maxParticipants != null && <span className="slc-capacity"> / {num(club.maxParticipants)}</span>}
          </>
        )}
      </td>
      <td>
        {collapsed ? (
          '—'
        ) : (
          <>
            <span className="slc-fill">
              <i
                style={{
                  width: `${Math.max(0, Math.min(100, club.capacityUsed || 0))}%`,
                  background:
                    club.capacityUsed >= 90
                      ? 'var(--green-500)'
                      : club.capacityUsed < 35
                      ? 'var(--red-500)'
                      : 'var(--blue-400)'
                }}
              />
            </span>
            {pct(club.capacityUsed)}
          </>
        )}
      </td>
      <td>{!collapsed && <ClubSpark series={club.series} />}</td>
      <td>
        {collapsed ? (
          '—'
        ) : (
          <span className={club.markCoverage != null && club.markCoverage < target ? 'slc-registerWarning' : ''}>
            {pct(club.markCoverage)}
          </span>
        )}
      </td>
      <td>
        {!collapsed && club.signal && (
          <span className={'slc-signal ' + (club.signalSeverity === 'opportunity' ? 'up' : 'dn')}>{club.signal}</span>
        )}
      </td>
    </tr>
  );
  return (
    <div className="slc-content">
      <div className="slo-kpis">
        {items.map(item => {
          const noSessions = !report.totalSessions && item.key !== 'decisions',
            value = noSessions ? null : item.value;
          return (
            <button
              type="button"
              key={item.key}
              className={'slo-kpi sli-kpi ' + (value == null ? 'unavailable' : '')}
              onClick={item.action}
            >
              <span className="slo-kpiLabel">
                <span className="slo-kpiLabelText">{item.label}</span>
                {value != null &&
                  (item.key === 'decisions' ? (
                    <span className="slo-delta neutral">of {report.count}</span>
                  ) : item.key === 'coverage' && value < target ? (
                    <span className="slo-delta wr">Below {target}%</span>
                  ) : item.delta != null ? (
                    <span className={'slo-delta ' + (item.delta >= 0 ? 'up' : 'dn')}>
                      {item.delta > 0 ? '+' : item.delta < 0 ? '−' : ''}
                      {num(Math.abs(item.delta))} pts
                    </span>
                  ) : null)}
              </span>
              <span className="slo-kpiValue">
                <strong>{item.unit === 'percent' ? pct(value) : num(value)}</strong>
              </span>
              <small>{noSessions ? 'No club sessions in this period' : item.sub}</small>
              <span className="slo-chevron" aria-hidden="true">
                ›
              </span>
            </button>
          );
        })}
      </div>
      <div className="slc-provision">
        <Card title="When clubs run" subtitle="Number of clubs per slot · empty slots are gaps in provision">
          <div className="slc-timetable">
            {!report.totalSessions ? (
              <p>Timetable appears once sessions are scheduled in this period.</p>
            ) : (
              <table className="slc-heat">
                <thead>
                  <tr>
                    <th></th>
                    {slots.map(label => (
                      <th key={label}>{label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.timetable.map((row: any[], day: number) => (
                    <tr key={day}>
                      <th>{days[day]}</th>
                      {row.map(cell => {
                        const v = Math.min(6, cell.count);
                        return (
                          <td key={cell.column}>
                            <button
                              type="button"
                              aria-label={`${days[day]} · ${slots[cell.column]}: ${cell.count} clubs`}
                              aria-pressed={slot?.key === `${day}:${cell.column}`}
                              onClick={() => {
                                setSlot({
                                  key: `${day}:${cell.column}`,
                                  label: `${days[day]} · ${slots[cell.column]}`
                                });
                                table.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }}
                              className={!cell.count ? 'empty' : ''}
                              style={
                                cell.count
                                  ? {
                                      background: `oklch(${0.95 - v * 0.08} ${0.03 + v * 0.025} 245)`,
                                      color: v >= 3 ? 'var(--slate-0)' : 'var(--slate-900)'
                                    }
                                  : {}
                              }
                            >
                              {cell.count || 'None'}
                            </button>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
        <Card
          title="Mix of clubs"
          subtitle={
            report.mixBy === 'category' ? (
              'Clubs and share of students reached'
            ) : (
              <>
                By activity · categories not{' '}
                <button type="button" className="sli-link" onClick={onSetup}>
                  set up
                </button>
              </>
            )
          }
        >
          <div className="slc-mix">
            {report.mix.map(group => (
              <button
                type="button"
                className="slc-mixRow"
                key={group.label}
                onClick={() => {
                  setSlot({ key: 'mix:' + group.label, label: group.label, clubIds: group.clubIds });
                  table.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
              >
                <b>{group.label}</b>
                <span className="slc-mixTrack">
                  <i style={{ width: `${group.reach || 0}%` }} />
                </span>
                <span>
                  <strong>{pct(group.reach)}</strong> · {group.clubs} clubs
                </span>
              </button>
            ))}
            {!report.mix.length && (
              <p className="slc-muted">Club mix appears once sessions are scheduled in this period.</p>
            )}
          </div>
        </Card>
      </div>
      <section className="slo-card slc-allClubs" ref={table}>
        <header className="slo-cardHead">
          <div>
            <h2>All clubs</h2>
            <p>Click a club to see registers and members</p>
          </div>
          <div className="slo-cardAction">
            <div className="sli-density" aria-label="Club sorting">
              {[
                ['attn', 'Needs attention'],
                ['busy', 'Busiest'],
                ['az', 'A–Z']
              ].map(([key, label]) => (
                <button
                  type="button"
                  key={key}
                  className={sort === key ? 'on' : ''}
                  aria-pressed={sort === key}
                  onClick={() => changeSort(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </header>
        {slot && (
          <div className="slc-filterChip">
            <button type="button" onClick={() => setSlot(null)} aria-label={`Remove ${slot.label} filter`}>
              {slot.label} ×
            </button>
          </div>
        )}
        <div className="slc-tableWrap">
          <table className="slc-table">
            <thead>
              <tr>
                {['Club', 'Avg attendance', 'Capacity used', 'Last 6 weeks', 'Registers taken', 'Signal'].map(label => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!report.totalSessions ? (
                <tr>
                  <td colSpan={6}>
                    <div className="slc-empty">
                      <p>No club sessions in this period. Change the period or check that sessions are scheduled.</p>
                      <button type="button" className="sli-button" onClick={onPeriod}>
                        Change period
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {sorted.filter(club => !slot?.clubIds || slot.clubIds.includes(club.id)).map(club => renderRow(club))}
                  {!sorted.length && (
                    <tr>
                      <td colSpan={6}>No clubs match this slot.</td>
                    </tr>
                  )}
                  {!!idle.length && !slot && (
                    <>
                      <tr className="slc-idle">
                        <td colSpan={6}>
                          <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>
                            {idle.length} clubs have no sessions in this period <i className={expanded ? 'open' : ''} />
                          </button>
                        </td>
                      </tr>
                      {expanded && idle.map(club => renderRow(club, true))}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
export function ClubDrawerContent({
  club,
  coverageTarget = 90,
  timezone = 'UTC',
  onMembers,
  onContact,
  canStudents,
  canMessage
}: {
  club: any;
  coverageTarget?: number;
  timezone?: string;
  onMembers: () => void;
  onContact: () => void;
  canStudents: boolean;
  canMessage: boolean;
}) {
  const missing = club.markCoverage != null && club.markCoverage < coverageTarget;
  return (
    <div className="slc-clubDrawer">
      <div className="slc-drawerContent">
        <h2>{club.name}</h2>
        <p className="slc-drawerIntro">
          Led by <span className={!club.leader ? 'slc-noLead' : ''}>{club.leader || 'No lead set'}</span> · capacity{' '}
          {num(club.maxParticipants)}
        </p>
        <div className="slc-miniStats">
          {[
            ['Avg attendance', num(club.averageAttendance)],
            ['Capacity used', pct(club.capacityUsed)],
            ['Registers', pct(club.markCoverage)]
          ]
            .filter(([, value]) => value != null)
            .map(([label, value]) => (
              <div key={label}>
                <span>{label}</span>
                <b>{value}</b>
              </div>
            ))}
        </div>
        <div className="slc-evidence">
          <h3>Attendance, last 6 weeks</h3>
          <ClubSpark series={club.series} width={460} height={70} />
        </div>
        <h3>Sessions</h3>
        <table className="slc-sessionTable">
          <tbody>
            {club.sessionLog.map(session => (
              <tr key={session.id}>
                <td>
                  {new Date(session.at).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    timeZone: timezone
                  })}
                </td>
                <td>
                  {!session.hasMarks ? (
                    <span className="slc-registerWarning">No register</span>
                  ) : (
                    `${num(session.present)} present`
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!club.sessionLog.length && <p className="slc-muted">No ended sessions in this period.</p>}
        <h3>Who comes</h3>
        <div className="slc-who">
          {[
            ['PP', club.who.pp],
            ['SEND', club.who.send],
            ['Girls', club.who.girls]
          ]
            .filter(([, value]) => value != null)
            .map(([label, value]) => (
              <span
                key={label as string}
                title={
                  value == null
                    ? 'Fewer than 10 students or no confirmed characteristic — hidden'
                    : 'Share of students with a known characteristic; unknown values are excluded'
                }
              >
                {label} {value == null ? '—' : pct(value)}
              </span>
            ))}
        </div>
      </div>
      <footer className="slc-drawerFooter">
        <button
          type="button"
          className="sli-button sli-primary"
          disabled={!canMessage || !club.leader}
          title={!canMessage ? 'This SLT role has no permission to send operational messages' : undefined}
          onClick={onContact}
        >
          {missing ? 'Nudge leader to take registers' : 'Message leader'}
        </button>
        <button type="button" className="sli-button" disabled={!canStudents || club.suppressed} onClick={onMembers}>
          See members
        </button>
      </footer>
    </div>
  );
}
export function ClubsExport({
  report,
  school,
  period,
  onCsv
}: {
  report: any;
  school: string;
  period: string;
  onCsv: () => void;
}) {
  return (
    <div className="sli-printSummary">
      <h2>Clubs summary</h2>
      <p>
        {school} · {period}
      </p>
      <p>
        {report.count} clubs · {num(report.capacity)} places
      </p>
      <p>
        Students in a club: {pct(report.reach)} · Capacity used: {pct(report.capacityUsed)} · Clubs needing a decision:{' '}
        {report.decisions} · Register coverage: {pct(report.coverage)}
      </p>
      <button type="button" className="sli-button sli-primary" onClick={event => printSltReport(event.currentTarget)}>
        Print / save as PDF
      </button>{' '}
      <details>
        <summary>Other downloads</summary>
        <button type="button" className="sli-button" onClick={onCsv}>
          Clubs table (CSV)
        </button>
      </details>
      <table className="slc-table">
        <thead>
          <tr>
            <th>Club</th>
            <th>Avg attendance</th>
            <th>Capacity</th>
            <th>Capacity used</th>
            <th>Registers</th>
            <th>Signal</th>
          </tr>
        </thead>
        <tbody>
          {report.items
            .filter(club => club.sessions)
            .map(club => (
              <tr key={club.id}>
                <td>{club.name}</td>
                <td>{num(club.averageAttendance)}</td>
                <td>{num(club.maxParticipants)}</td>
                <td>{pct(club.capacityUsed)}</td>
                <td>{pct(club.markCoverage)}</td>
                <td>{club.signal || ''}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}
